import React, { useEffect, useState } from "react"
import { Input, Button, Space, Select, Row, Col, Upload, message } from 'antd'
import { MinusCircleOutlined, DeleteOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons'
import { baseAxiosNoInfo } from "../../tool/axios"
import { apiPrefix } from "../../js/config"
import _ from 'lodash'

const { Option } = Select

type editStandardsParams = {
    id?: string | undefined,
    goods_specs_ids: string,
    goods_specs_names: string,
    goods_specs_values: string,
    sku_code: string,
    sku_pic_url: string,
    inventory_num: number,
    sale_price: number,
    your_price: number
}

interface StandardsDetailProp {
    ref?: any
    spuID?: string
    spuData?: { spuID: string | undefined, goodsStandardData: editStandardsParams[] }
    pid?: string
    value?: editStandardsParams[]
    onChange?: (e: editStandardsParams[]) => void
}

type standard = {
    id?: string,
    valID: string[],
    valText: string[],
    sku_code: string,
    sku_pic_url: string,
    inventory_num: number,
    sale_price: number,
    your_price: number
}

type specsValueOption = {
    "id": string,
    "specs_value": string
}

const StandardsDetail: React.FC<StandardsDetailProp> = ({ spuData = {}, spuID = '', pid = '', value = [], onChange }) => {

    let standard = {
        id: undefined,
        valID: [''],
        valText: [''],
        sku_code: '',
        sku_pic_url: '',
        inventory_num: 0,
        sale_price: 0,
        your_price: 0
    }

    const [fetched, setFetched] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)

    const [specsNames, setSpecsNames] = useState<string[]>([''])
    const [standards, setStandards] = useState<standard[]>([{ ...standard }])

    const [specsNameOptions, setSpecsNameOptions] = useState<string[]>([])
    const [specsValOptions, setSpecsValOptions] = useState<specsValueOption[][][]>([[]])

    const triggerChange = (changeVal: { specsNames?: string[], standards?: standard[] }) => {
        let nameData = changeVal.specsNames || specsNames
        let standardsData = changeVal.standards || standards

        let postData = standardsData?.map((el: standard) => {
            return {
                id: el.id,
                goods_specs_ids: el.valID.join(','),
                goods_specs_names: nameData?.join(',') || '',
                goods_specs_values: el.valText.join(','),
                sku_code: el.sku_code,
                sku_pic_url: el.sku_pic_url,
                inventory_num: el.inventory_num,
                sale_price: el.sale_price,
                your_price: el.your_price
            }
        })

        onChange?.(postData)
    }

    const namesChange = (names: string[]) => {
        triggerChange({ specsNames: names })
    }

    const valuesChange = (standardsArr: standard[]) => {
        triggerChange({ standards: standardsArr })
    }

    const inputChange = (standardsArr: standard[]) => {
        triggerChange({ standards: standardsArr })
    }

    // 获取规格名称
    useEffect(() => {
        baseAxiosNoInfo('/global/specs_name_list').then((res: any) => {
            if (res) { setSpecsNameOptions(res.map((el: { specs_name: string }) => el.specs_name)) }
        })
    }, [])

    // 编辑场景的初始化
    useEffect(() => {
        async function fetchValOptionsData() {
            if (pid) {
                let names: string[] = value[0].goods_specs_names.split(',')
                let opts: any = _.cloneDeep(specsValOptions)

                for (let i = 0; i < names.length; i++) {
                    opts[0][i] = await baseAxiosNoInfo('/global/specs_value_list', {
                        params: {
                            specs_name: names[i],
                            spu_id: ''
                        }
                    })
                }

                setSpecsValOptions(opts)
            }
        }

        if (value && value.length > 0 && pid && !fetched) {
            setFetched(true)

            standards[0].id = value[0].id
            standards[0].sku_code = value[0].sku_code
            standards[0].inventory_num = value[0].inventory_num
            standards[0].sale_price = value[0].sale_price
            standards[0].sku_pic_url = value[0].sku_pic_url
            standards[0].your_price = value[0].your_price

            if (value[0].goods_specs_names) {
                let goodsIDs = value[0].goods_specs_ids.split(',')
                let specsNameVals = value[0].goods_specs_values.split(',')
                let names: string[] = value[0].goods_specs_names.split(',')

                setSpecsNames(names)

                let s: standard[] = _.cloneDeep(standards)
                for (let i = 0; i < names.length - 1; i++) {
                    if (s[0].valID.length < names.length) {
                        s[0].valID.push('')
                        s[0].valText.push('')
                    }
                }

                for (let i = 0; i < s[0].valID.length; i++) {
                    s[0].valID[i] = goodsIDs[i]
                    s[0].valText[i] = specsNameVals[i]
                }

                setStandards(s)
                fetchValOptionsData()
            }
        }
    }, [value])

    const handleAddKey = () => {
        if (specsNames.length === 2) {
            message.error('目前限制两个规格')
            return
        }

        let specsNamesArr = [...specsNames]
        specsNamesArr.push('')

        setSpecsNames(specsNamesArr)
        namesChange(specsNamesArr)

        // 同步数据列
        let standardsArr = _.cloneDeep(standards)
        if (specsNamesArr.length > standards[0].valID.length && specsNamesArr.length > standards[0].valText.length) {
            standardsArr.forEach(el => {
                let newIds = [...el.valID]
                if (newIds.length < specsNamesArr.length) {
                    el.valID.push('')
                }
            })

            setStandards(standardsArr)
        }
    }

    const handleAddStandard = () => {
        let arr = _.cloneDeep(standards)
        let row = _.cloneDeep(arr[0])

        row.id = undefined
        row.valID = row.valID.map((el: string) => '')
        row.valText = row.valText.map((el: string) => '')
        row.sku_code = ''
        row.sku_pic_url = ''
        row.inventory_num = 0
        row.sale_price = 0
        row.your_price = 0
        arr.push(row)

        setStandards(arr)
        valuesChange(arr)

        // 规格值下拉选项数组的个数同步
        let vOptions = _.cloneDeep(specsValOptions)
        let firstOption = _.cloneDeep(vOptions[0])

        vOptions.push(firstOption)
        setSpecsValOptions(vOptions)
    }

    const handleDelteKey = (index: number) => {
        if (specsNames.length === 1) {
            message.error('最少要有一个规格名')
            return
        }

        let keys = [...specsNames]
        keys.splice(index, 1)

        setSpecsNames(keys)
        namesChange(keys)

        let standardsArr = _.cloneDeep(standards)
        if (keys.length < standardsArr[0].valID.length) {
            standardsArr.forEach(el => {
                el.valID.splice(index, 1)
                el.valText.splice(index, 1)
            })

            setStandards(standardsArr)
        }
    }

    const deleteStandards = (index: number) => {
        if (standards.length === 1) {
            message.error('最少要有一个规格值')
            return
        }

        let arr = _.cloneDeep(standards)
        arr.splice(index, 1)

        setStandards(arr)
        valuesChange(arr)
    }

    const uploadButton = (
        <div >
            {loading ? <LoadingOutlined /> : <PlusOutlined />}
        </div>
    )

    const handleSpecsNameChange = (v: string, rowIndex: number) => {
        let specsNamesArr = _.cloneDeep(specsNames)

        if (specsNamesArr.includes(v)) {
            message.error('不能选择重复的规格名称')

        } else {
            specsNamesArr[rowIndex] = v

            namesChange(specsNamesArr)
            setSpecsNames(specsNamesArr)

            baseAxiosNoInfo('/global/specs_value_list', {
                params: {
                    specs_name: v,
                    spu_id: spuID
                }

            }).then((res: any) => {
                if (res) {
                    let s = _.cloneDeep(specsValOptions)
                    s.forEach((el: any) => {
                        el[rowIndex] = res
                    })

                    setSpecsValOptions(s)
                }

                // 清除已选规格值
                let newStandards = _.cloneDeep(standards)
                for (let i = 0; i < newStandards.length; i++) {
                    newStandards[i].valID[rowIndex] = ''
                    newStandards[i].valText[rowIndex] = ''
                }

                setStandards(newStandards)
                valuesChange(newStandards)
            })
        }
    }

    const handleSpecsValChange = (v: any, rowIndex: number, valIndex: number) => {
        let standardsArr = _.cloneDeep(standards)
        standardsArr[rowIndex].valID[valIndex] = v.value
        standardsArr[rowIndex].valText[valIndex] = v.label

        valuesChange(standardsArr)
        setStandards(standardsArr)
    }

    const handleStandardRowInputChange = (e: any, rowIndex: number, attr: string) => {
        let standardsArr: any = _.cloneDeep(standards)
        standardsArr[rowIndex][attr] = e.target.value

        inputChange(standardsArr)
        setStandards(standardsArr)
    }

    const handleUploadChange = (e: any, rowIndex: number) => {
        if (e.fileList[0].response && e.fileList[0].response.status === 0) {
            let standardsArr: any = _.cloneDeep(standards)
            standardsArr[rowIndex]['sku_pic_url'] = e.fileList[0].response.data[0]

            setStandards(standardsArr)
            triggerChange({ specsNames, standards: standardsArr })
        }
    }

    return <div className="clear standards-detail">
        <Row gutter={18}>
            {specsNames.map((el: any, index: number) => <Col style={{ marginTop: 3 }}>
                <Space>
                    <Select disabled={pid ? true : false} placeholder="规格名称" style={{ width: 120 }} value={el} onChange={(v: string) => { handleSpecsNameChange(v, index) }}>
                        {specsNameOptions.map((el: any) => <Option value={el} key={el}>{el}</Option>)}
                    </Select>

                    {!pid && <DeleteOutlined onClick={() => { handleDelteKey(index) }} />}
                </Space>
            </Col>)}

            <Col>产品编号（品牌方）</Col>
            <Col>图片</Col>
            <Col>库存</Col>
            <Col>供货价（渠道方）</Col>
            <Col>零售价（品牌方）</Col>

            <Col style={{ marginTop: 3 }}>{!pid && <Button onClick={handleAddKey}>添加规格名</Button>}</Col>
        </Row>

        {standards.map((el: any, rowIndex: number) => <Row gutter={18} style={{ marginTop: 10 }} key={el}>
            {el.valID.map((el2: any, valIndex: number) => <Col style={{ marginTop: 3 }}>
                <Select placeholder="规格值" style={{ width: 120 }} value={{ value: standards[rowIndex].valID[valIndex], label: standards[rowIndex].valText[valIndex] }} labelInValue={true} onChange={(v: any) => {
                    handleSpecsValChange(v, rowIndex, valIndex)
                }}>
                    {specsValOptions[rowIndex] && specsValOptions[rowIndex][valIndex] && specsValOptions[rowIndex][valIndex].map((el3: any) => <Option value={el3.id} key={el3.id}>{el3.specs_value}</Option>)}
                </Select>
            </Col>)}

            <Col><Input size="small" style={{ maxWidth: 130 }} onChange={(e: any) => { handleStandardRowInputChange(e, rowIndex, 'sku_code') }} value={standards[rowIndex]['sku_code']} /></Col>

            <Col style={{ marginTop: 5 }}>
                <Upload
                    name="files"
                    action={apiPrefix + '/global/upload_pic'}
                    listType="picture-card"
                    className="avatar-uploader"
                    showUploadList={false}
                    onChange={(file: any) => { handleUploadChange(file, rowIndex) }}
                >
                    {standards[rowIndex].sku_pic_url ? <img src={standards[rowIndex].sku_pic_url} alt="avatar" style={{ width: '100%' }} /> : uploadButton}
                </Upload>
            </Col>

            <Col><Input size="small" style={{ maxWidth: 120 }} type="number" onChange={(e: any) => { handleStandardRowInputChange(e, rowIndex, 'inventory_num') }} value={standards[rowIndex]['inventory_num']} /></Col>
            <Col><Input size="small" style={{ maxWidth: 120 }} type="number" onChange={(e: any) => { handleStandardRowInputChange(e, rowIndex, 'sale_price') }} value={standards[rowIndex]['sale_price']} /></Col>
            <Col><Input size="small" style={{ maxWidth: 120 }} type="number" onChange={(e: any) => { handleStandardRowInputChange(e, rowIndex, 'your_price') }} value={standards[rowIndex]['your_price']} /></Col>

            <Col>{!el.id && <MinusCircleOutlined style={{ cursor: 'pointer', marginTop: 5 }} onClick={() => { deleteStandards(rowIndex) }} />}</Col>
        </Row>)}

        <div className="standards-line"></div>

        <Button style={{ marginTop: 15 }} size="small" onClick={handleAddStandard}>新增商品规格</Button>
    </div>
}

export default StandardsDetail